/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import * as Msdyn365 from '@msdyn365-commerce/core';
import { Node } from '@msdyn365-commerce-modules/utilities';
import React from 'react';

import classnames from 'classnames';

import { SalesOrder } from '@msdyn365-commerce/retail-proxy';

/**
 * Class names used in component.
 */
const componentClassName = 'ms-order-history-with-search-and-filters-header';
const headingClassName = `${componentClassName}__heading`;
const orderCountClassName = `${componentClassName}__order-count`;

/**
 * IOrderHistoryWithSearchAndFiltersHeaderResources interface.
 */
export interface IOrderHistoryWithSearchAndFiltersHeaderResources {
    orderCountLabel: string;
    ordersCountLabel: string;
}

/**
 * IOrderHistoryWithSearchAndFiltersHeaderData interface.
 */
export interface IOrderHistoryWithSearchAndFiltersHeaderData {
    salesOrders: SalesOrder[];
}

/**
 * IOrderHistoryWithSearchAndFiltersHeaderProps interface.
 */
export interface IOrderHistoryWithSearchAndFiltersHeaderProps
    extends Msdyn365.IComponentProps<IOrderHistoryWithSearchAndFiltersHeaderData> {
    className?: string;
    resources: IOrderHistoryWithSearchAndFiltersHeaderResources;
    textProps: Msdyn365.ITextProps;
    showOrderCount: boolean;
    extraActions?: React.ReactNode;
    onChange(event: Msdyn365.ContentEditableEvent): void;
}

/**
 * OrderHistoryWithSearchAndFiltersHeader component.
 * @param props - Configuration of the component.
 * @returns React node.
 */
const OrderHistoryWithSearchAndFiltersHeaderFunctionalComponent: React.FC<IOrderHistoryWithSearchAndFiltersHeaderProps> = (
    props: IOrderHistoryWithSearchAndFiltersHeaderProps
) => {
    const {
        resources,
        data: { salesOrders },
        textProps,
        showOrderCount,
        extraActions
    } = props;

    const orderCount = salesOrders?.length;
    const orderCountText = `(${orderCount} ${orderCount === 1 ? resources?.orderCountLabel : resources?.ordersCountLabel})`;

    return (
        <Node className={classnames(props.className, componentClassName)}>
            {textProps?.text && (
                <Msdyn365.Text
                    {...textProps}
                    className={headingClassName}
                    tag={textProps.tag || 'h2'}
                    editProps={{ onEdit: props.onChange, requestContext: props.context.request }}
                />
            )}
            {showOrderCount && <span className={orderCountClassName}>{orderCountText}</span>}
            {extraActions}
        </Node>
    );
};

/**
 * OrderHistoryWithSearchAndFiltersHeader component.
 */
export const OrderHistoryWithSearchAndFiltersHeaderComponent: React.FunctionComponent<IOrderHistoryWithSearchAndFiltersHeaderProps> = Msdyn365.msdyn365Commerce.createComponent<
    // @ts-expect-error
    IOrderHistoryWithSearchAndFiltersHeaderProps
>('OrderHistoryWithSearchAndFiltersHeaderComponent', { component: OrderHistoryWithSearchAndFiltersHeaderFunctionalComponent });
